import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Package, Pencil, Plus, Star } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { OfferStatusBadge } from "~/components/offer-status-badge";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Modal } from "~/components/ui/modal/modal";
import { PageSection } from "~/components/ui/page-section";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import {
  SUMMIT_OFFER_STATUS_QUERY_KEY,
  createNewMiniSprint,
  createNewOffer,
  deleteOffer,
  getCoachMiniSprints,
  getCoachOffer,
  getCoachOffers,
  getSummitOfferStatuses,
  openSummitSpecial,
  resolveStatusLabel,
} from "~/lib/functions/coach-offers";
import { makeMainOffer } from "~/lib/functions/offer-lifecycle";
import { MembershipPlan, OfferStatus } from "~/lib/schemas/db-enums";
import { canBeMainOffer } from "~/lib/shared/offer-lifecycle";
import { statusForSummit } from "~/lib/shared/summit-special";
import { formatSummitDateRange } from "~/lib/summit";

export const Route = createFileRoute("/_user/my-coaching/profile/offers/")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["coach", "main-offer"],
        queryFn: () => getCoachOffer(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["coach-offers"],
        queryFn: () => getCoachOffers(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: SUMMIT_OFFER_STATUS_QUERY_KEY,
        queryFn: () => getSummitOfferStatuses(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["coach-mini-sprints"],
        queryFn: () => getCoachMiniSprints(),
      }),
    ]);
  },
  component: OffersPage,
});

function OffersPage() {
  const coach = useCoachPrivateProfileRequired();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasSummits = coach.summitOfferSummits.length > 0;
  const isProPlus = coach.membershipPlan === MembershipPlan.ProPlus;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<string | undefined>();

  const { data: mainOffer } = useQuery({
    queryKey: ["coach", "main-offer"],
    queryFn: () => getCoachOffer(),
  });

  const { data: offersData } = useQuery({
    queryKey: ["coach-offers"],
    queryFn: () => getCoachOffers(),
  });
  // The main slot shows the main offer, or the draft/retired offer that will
  // become it; every other standard offer is listed below.
  const otherOffers = (offersData?.offers ?? []).filter(
    (offer) => offer.id !== mainOffer?.id,
  );

  const { data: summitStatuses = [] } = useQuery({
    queryKey: SUMMIT_OFFER_STATUS_QUERY_KEY,
    queryFn: () => getSummitOfferStatuses(),
  });

  const { data: miniSprintData } = useQuery({
    queryKey: ["coach-mini-sprints"],
    queryFn: () => getCoachMiniSprints(),
  });
  const miniSprints = miniSprintData?.offers ?? [];

  const setMainMutation = useMutation({
    mutationFn: (offerId: string) => makeMainOffer({ data: { offerId } }),
    onSuccess: () => {
      toast.success("Main offer updated");
      queryClient.invalidateQueries({ queryKey: ["coach-offers"] });
      queryClient.invalidateQueries({ queryKey: ["coach", "main-offer"] });
    },
    onError: (error) =>
      toast.error(error.message || "Failed to update main offer"),
  });

  const createMutation = useMutation({
    mutationFn: () => createNewOffer(),
    onSuccess: (result) => {
      void navigate({
        to: "/my-coaching/profile/offers/$offerId",
        params: { offerId: result.offerId },
      });
      toast.success("Offer created");
    },
    onError: () => toast.error("Failed to create offer"),
  });

  const deleteOfferMutation = useMutation({
    mutationFn: (offerId: string) => deleteOffer({ data: { offerId } }),
    onSuccess: () => {
      toast.success("Offer deleted");
      queryClient.invalidateQueries({ queryKey: ["coach-offers"] });
      queryClient.invalidateQueries({ queryKey: ["coach-mini-sprints"] });
      setShowDeleteModal(false);
      setOfferToDelete(undefined);
    },
    onError: () => toast.error("Failed to delete offer"),
  });

  const createMiniSprintMutation = useMutation({
    mutationFn: () => createNewMiniSprint(),
    onSuccess: (result) => {
      void navigate({
        to: "/my-coaching/profile/offers/$offerId",
        params: { offerId: result.offerId },
      });
      toast.success("Mini Sprint created");
    },
    onError: () => toast.error("Failed to create Mini Sprint"),
  });

  const openSummitSpecialMutation = useMutation({
    mutationFn: (summitId: string) => openSummitSpecial({ data: { summitId } }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: SUMMIT_OFFER_STATUS_QUERY_KEY,
      });
      void navigate({
        to: "/my-coaching/profile/offers/$offerId",
        params: { offerId: result.offerId },
      });
    },
    onError: (error) =>
      toast.error(error.message || "Failed to open the summit special"),
  });

  const requestDelete = (offerId: string) => {
    setOfferToDelete(offerId);
    setShowDeleteModal(true);
  };

  const navigateToEdit = (offerId: string) => {
    void navigate({
      to: "/my-coaching/profile/offers/$offerId",
      params: { offerId },
    });
  };

  return (
    <div className="space-y-8">
      <PageSection
        title="Main offer"
        description="Your primary coaching offer shown on your profile"
      >
        {mainOffer ? (
          <OfferCard
            preHeadline={mainOffer.preHeadline}
            headline={mainOffer.headline || "Untitled offer"}
            onOpen={() => navigateToEdit(mainOffer.id)}
            badge={
              mainOffer.isMain ? (
                <Badge variant="secondary">Main</Badge>
              ) : (
                <OfferStatusBadge status={mainOffer.status} />
              )
            }
          />
        ) : (
          <EmptyState
            size="sm"
            icon={Package}
            title="No main offer yet"
            description="Set up your primary coaching offer to feature it on your profile."
          />
        )}
      </PageSection>

      {/* Additional offers (ProPlus only) */}
      <PageSection
        title="Additional offers"
        description="Extra packages available on your profile"
        action={
          isProPlus && (
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              New offer
            </Button>
          )
        }
      >
        {!isProPlus ? (
          <p className="text-text-secondary rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm">
            Additional offers are available on the ProPlus plan.
          </p>
        ) : otherOffers.length === 0 ? (
          <EmptyState
            size="sm"
            icon={Package}
            title="No additional offers yet"
            description="Create extra packages to give clients more ways to work with you."
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              New offer
            </Button>
          </EmptyState>
        ) : (
          <ul className="space-y-4">
            {otherOffers.map((offer) => (
              <li key={offer.id}>
                <OfferCard
                  preHeadline={offer.preHeadline}
                  headline={offer.headline || "Untitled offer"}
                  onOpen={() => navigateToEdit(offer.id)}
                  badge={
                    offer.status !== OfferStatus.Published && (
                      <OfferStatusBadge status={offer.status} />
                    )
                  }
                >
                  {canBeMainOffer(offer) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMainMutation.mutate(offer.id);
                      }}
                      disabled={setMainMutation.isPending}
                      title="Set as main offer"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <DeleteOfferButton onDelete={() => requestDelete(offer.id)} />
                </OfferCard>
              </li>
            ))}
          </ul>
        )}
      </PageSection>

      {/* Mini Sprints (all coaches) */}
      <PageSection
        title="Mini Sprints"
        description="Free, booking-first sprints with their own shareable page"
        action={
          <Button
            onClick={() => createMiniSprintMutation.mutate()}
            disabled={createMiniSprintMutation.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Mini Sprint
          </Button>
        }
      >
        {miniSprints.length === 0 ? (
          <EmptyState
            size="sm"
            icon={Package}
            title="No Mini Sprints yet"
            description="Spin up a free, booking-first sprint with its own shareable page."
          >
            <Button
              variant="primary"
              size="sm"
              onClick={() => createMiniSprintMutation.mutate()}
              disabled={createMiniSprintMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Mini Sprint
            </Button>
          </EmptyState>
        ) : (
          <ul className="space-y-4">
            {miniSprints.map((offer) => (
              <li key={offer.id}>
                <OfferCard
                  preHeadline={offer.preHeadline}
                  headline={offer.headline || "Untitled Mini Sprint"}
                  onOpen={() => navigateToEdit(offer.id)}
                  badge={
                    offer.status !== OfferStatus.Published && (
                      <OfferStatusBadge status={offer.status} />
                    )
                  }
                >
                  <DeleteOfferButton onDelete={() => requestDelete(offer.id)} />
                </OfferCard>
              </li>
            ))}
          </ul>
        )}
      </PageSection>

      {/* Summit specials */}
      {hasSummits && (
        <PageSection
          title="Summit specials"
          description="Special offers for summits you're participating in"
        >
          <ul className="space-y-4">
            {coach.summitOfferSummits.map((summit) => {
              const status = statusForSummit(summitStatuses, summit.id);
              const { label, variant, className } = resolveStatusLabel(status);
              return (
                <li key={summit.id}>
                  <OfferCard
                    headline={summit.title}
                    detail={formatSummitDateRange(
                      summit.startDate,
                      summit.endDate,
                    )}
                    onOpen={() => {
                      if (status) {
                        navigateToEdit(status.offerId);
                      } else if (!openSummitSpecialMutation.isPending) {
                        openSummitSpecialMutation.mutate(summit.id);
                      }
                    }}
                    badge={
                      <Badge variant={variant} className={className}>
                        {label}
                      </Badge>
                    }
                  />
                </li>
              );
            })}
          </ul>
        </PageSection>
      )}

      <Modal.Root open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <Modal.Panel>
          <Modal.Title>Delete offer</Modal.Title>
          <Modal.Description>
            Are you sure you want to delete this offer? This action cannot be
            undone.
          </Modal.Description>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              onClick={() => {
                setOfferToDelete(undefined);
                setShowDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteOfferMutation.isPending}
              onClick={() => {
                if (offerToDelete) deleteOfferMutation.mutate(offerToDelete);
              }}
            >
              Delete
            </Button>
          </div>
        </Modal.Panel>
      </Modal.Root>
    </div>
  );
}

/**
 * An offer row that opens the offer editor; `detail` is a line under the
 * headline and `children` are trailing actions.
 */
function OfferCard({
  preHeadline,
  headline,
  detail,
  onOpen,
  badge,
  children,
}: {
  preHeadline?: string | null;
  headline: string;
  detail?: string;
  onOpen: () => void;
  badge?: ReactNode;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <Card
      variant="interactive"
      className="cursor-pointer flex-row items-start justify-between p-4"
      onClick={onOpen}
    >
      <div className="min-w-0 space-y-1">
        {preHeadline && (
          <p className="text-text-tertiary text-sm">{preHeadline}</p>
        )}
        <p className="text-base font-medium text-gray-900">{headline}</p>
        {detail && <p className="text-text-tertiary text-sm">{detail}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {badge}
        <Pencil className="text-text-tertiary h-4 w-4" />
        {children}
      </div>
    </Card>
  );
}

function DeleteOfferButton({
  onDelete,
}: {
  onDelete: () => void;
}): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
    >
      Delete
    </Button>
  );
}
